import { useCallback } from 'react';
import {
  CandidateRow,
  ImportRowDraft,
} from '@shared/models/element-import.model';
import {
  ProjectChangeType,
  type ProjectChange,
} from '@shared/helpers/pipeline/update-engine.models';
import type { CsvImportTrigger } from '@shared/models/project.model';
import { getSelectedVersion } from '@client/store/ui';
import { useProductsLookup } from '@client/store/product';
import { useRecipeLookup } from '@client/store/recipe/recipe.hook';
import { useProjectState } from '@client/store/project';
import { useApplyProjectChanges } from '@client/store/project/use-apply-project-changes.hook';
import { useSnackbar } from '@client/hooks/use-snackbar.hooks';
import {
  candidateUnitFrom,
  tryMaterializeCandidateRow,
} from '@shared/helpers/materialize-candidate.helpers';
import {
  applyCandidateIfcMeasureOverride,
  resolveActiveIfcMeasure,
} from '@shared/helpers/ifc-quantity.helpers';
import {
  groupImportedElementAdds,
  IMPORT_REPLACE_NEEDS_PROPOSAL_MESSAGE,
} from '@shared/helpers/import-replace.helpers';
import { getElementImportsData } from './element-imports.store';
import { collectImportChanges } from './auto-import.helpers';
import { selectedProposalIdForVersion } from './import-replace-target.helpers';
import { buildUnmapImportRowsChanges } from './import-backspace.helpers';
import { collectImportedElementsInView } from './candidate-status.helpers';
import {
  buildDraftMappingsByRowId,
  buildIfcMeasureByRowId,
  buildImportRowDraftsChange,
  buildSelectedQuantitiesByRowId,
  draftToOriginMapping,
  getImportRowDraft,
  type ImportRowDraftWrite,
} from '@shared/helpers/import-row-draft.helpers';

/** Snackbar text for rows that threw while materializing, or `undefined`. */
export const buildFailedImportMessage = (
  failedRows: readonly CandidateRow[],
): string | undefined => {
  const [firstFailed, ...restFailed] = failedRows;
  if (!firstFailed) return undefined;
  const firstName = firstFailed.name ?? firstFailed.resourceCode;
  const extra = restFailed.length > 0 ? ` and ${restFailed.length} more` : '';
  return `Could not import "${firstName}"${extra}. Check the mapped product's category.`;
};

/**
 * Client-side auto-import (DEV-3298 phase 2).
 *
 * `buildImportChanges` is the pure builder: it materializes every importable row
 * (already-imported/discarded rows are skipped) and returns the project changes
 * without applying them. When `newImportId` is set (file upload) the id is folded
 * into the same {@link VersionImports} change, so the whole upload — importId ref,
 * element batch and prefill drafts — commits as a single undo frame.
 *
 * `importRows` is the convenience wrapper that applies those changes and shows
 * the failure snackbar; used by single-row imports and re-materialization.
 */
export const useAutoImportCandidates = (): {
  importRows: (rows: readonly CandidateRow[], trigger: 'auto' | 'user') => void;
  buildImportChanges: (
    rows: readonly CandidateRow[],
    trigger: 'auto' | 'user',
    newImportId?: string,
  ) => {
    changes: ProjectChange[];
    failedRows: CandidateRow[];
    needsProposal: boolean;
  };
  importRowWithDraft: (
    row: CandidateRow,
    patch: Partial<ImportRowDraft>,
    options?: { trigger?: Extract<CsvImportTrigger, 'user' | 'ai'> },
  ) => void;
  importRowsWithDrafts: (
    items: readonly {
      row: CandidateRow;
      patch: Partial<ImportRowDraft>;
    }[],
    options?: { trigger?: Extract<CsvImportTrigger, 'user' | 'ai'> },
  ) => void;
  unsetImportRow: (row: CandidateRow) => void;
} => {
  const { project } = useProjectState('project');
  const productsById = useProductsLookup();
  const recipesById = useRecipeLookup();
  const applyChanges = useApplyProjectChanges();
  const { enqueueSnackbar } = useSnackbar();

  const buildImportChanges = useCallback(
    (
      rows: readonly CandidateRow[],
      trigger: 'auto' | 'user',
      newImportId?: string,
    ): {
      changes: ProjectChange[];
      failedRows: CandidateRow[];
      needsProposal: boolean;
    } => {
      // Read the version fresh: callers import right after `updateDraft`, so a
      // version captured at render time would miss the draft just written and
      // materialize one edit behind (or skip the row entirely).
      const version = getSelectedVersion();
      if (!version || !project) {
        return { changes: [], failedRows: [], needsProposal: false };
      }
      // Read imports fresh: callers import right after `upsertMappings`, so a
      // value captured at render time would miss the mapping just saved.
      const importsData = getElementImportsData(project.id);
      const mappingsByCode = importsData?.mappingsByCode ?? {};
      const importsById = importsData?.importsById ?? {};
      // Carry each row's typed total quantity through as a count value override
      // (DEV-3369); only rows where the user typed one in-row contribute.
      const quantityOverridesByRowId: Record<string, number> = {};
      for (const row of rows) {
        const value = getImportRowDraft(version, row.id).totalQuantity;
        if (value !== undefined && Number.isFinite(value)) {
          quantityOverridesByRowId[row.id] = value;
        }
      }

      // Product-prefill drafts for skipped rows are folded into `changes` by
      // collectImportChanges (single VersionImports change), so no separate
      // per-row draft write is needed here.
      const { changes, failedRows } = collectImportChanges(
        rows,
        mappingsByCode,
        productsById,
        recipesById,
        version,
        importsById,
        buildDraftMappingsByRowId(version, rows),
        quantityOverridesByRowId,
        buildSelectedQuantitiesByRowId(version, rows),
        trigger,
        newImportId,
        buildIfcMeasureByRowId(version, rows),
      );
      const grouped = groupImportedElementAdds(
        version,
        selectedProposalIdForVersion(version),
        changes,
      );

      return {
        changes: grouped.changes,
        failedRows,
        needsProposal: grouped.needsProposal,
      };
    },
    [project, productsById, recipesById],
  );

  /**
   * Write one or more row draft patches and (re)try import as ONE batch — a
   * single undo frame (DEV-3457). A complete mapping materializes; an incomplete
   * one only writes the draft. Several rows share one VersionImports change so
   * later writes cannot clobber earlier ones.
   */
  const importRowsWithDrafts = useCallback(
    (
      items: readonly {
        row: CandidateRow;
        patch: Partial<ImportRowDraft>;
      }[],
      options?: { trigger?: Extract<CsvImportTrigger, 'user' | 'ai'> },
    ) => {
      const version = getSelectedVersion();
      if (!version || !project || items.length === 0) return;
      const imported = collectImportedElementsInView(version);
      const importsData = getElementImportsData(project.id);
      const trigger = options?.trigger ?? 'user';
      const elementChanges: ProjectChange[] = [];
      const draftWrites: Record<string, ImportRowDraftWrite> = {};
      let addedElement = false;

      for (const { row, patch } of items) {
        if (imported.has(row.id)) continue;
        const nextDraft = { ...getImportRowDraft(version, row.id), ...patch };
        const effectiveRow = applyCandidateIfcMeasureOverride(
          row,
          resolveActiveIfcMeasure(row, nextDraft.ifcMeasure),
        );
        const unitFrom = candidateUnitFrom(effectiveRow);
        const overlay = unitFrom
          ? draftToOriginMapping(nextDraft, unitFrom)
          : undefined;
        const totalQuantity = nextDraft.totalQuantity;
        const result = overlay
          ? tryMaterializeCandidateRow(row, {
              mappingsByCode: importsData?.mappingsByCode ?? {},
              productsById,
              recipesById,
              importsById: importsData?.importsById ?? {},
              draftMappingsByRowId: { [row.id]: overlay },
              quantityOverridesByRowId:
                totalQuantity !== undefined && Number.isFinite(totalQuantity)
                  ? { [row.id]: totalQuantity }
                  : undefined,
              selectedQuantityByRowId: nextDraft.selectedQuantity
                ? { [row.id]: nextDraft.selectedQuantity }
                : undefined,
              ifcMeasureByRowId: nextDraft.ifcMeasure
                ? { [row.id]: nextDraft.ifcMeasure }
                : undefined,
              trigger,
            })
          : ({ ok: false } as const);

        if (result.ok) {
          addedElement = true;
          elementChanges.push({
            type: ProjectChangeType.ElementAdd,
            elementId: version.id,
            factory: result.element,
            generateUniqueName: false,
          });
          draftWrites[row.id] = {
            mode: 'replace',
            draft: nextDraft.ifcMeasure
              ? { ifcMeasure: nextDraft.ifcMeasure }
              : null,
          };
        } else {
          draftWrites[row.id] = { mode: 'merge', patch };
        }
      }

      const draftChange = buildImportRowDraftsChange(version, draftWrites);
      const batched = draftChange
        ? [...elementChanges, draftChange]
        : elementChanges;
      const grouped = groupImportedElementAdds(
        version,
        selectedProposalIdForVersion(version),
        batched,
      );
      if (grouped.needsProposal) {
        enqueueSnackbar(IMPORT_REPLACE_NEEDS_PROPOSAL_MESSAGE, {
          variant: 'warning',
        });
        // The materialized rows were dropped. Keep incomplete draft edits, and
        // do not clear the drafts of rows whose elements were never added.
        const keptWrites: Record<string, ImportRowDraftWrite> = {};
        for (const [rowId, write] of Object.entries(draftWrites)) {
          if (write.mode !== 'merge') continue;
          keptWrites[rowId] = write;
        }
        const kept = buildImportRowDraftsChange(version, keptWrites);
        if (!kept) return;
        void applyChanges([kept], { skipEnrichment: true }).catch(() => {
          enqueueSnackbar('Import failed while applying changes', {
            variant: 'error',
          });
        });
        return;
      }
      const changes = grouped.changes;
      if (changes.length === 0) return;
      const applyOptions = addedElement
        ? {}
        : { skipEnrichment: true as const };
      void applyChanges(changes, applyOptions).catch(() => {
        enqueueSnackbar('Import failed while applying changes', {
          variant: 'error',
        });
      });
    },
    [project, productsById, recipesById, applyChanges, enqueueSnackbar],
  );

  const importRowWithDraft = useCallback(
    (
      row: CandidateRow,
      patch: Partial<ImportRowDraft>,
      options?: { trigger?: Extract<CsvImportTrigger, 'user' | 'ai'> },
    ) => {
      importRowsWithDrafts([{ row, patch }], options);
    },
    [importRowsWithDrafts],
  );

  /**
   * Drop a row's mapping and revert it to a bare candidate: the draft (carrier
   * of the in-row mapping) is cleared and a materialized element is removed.
   * Same builder as Backspace-unmap, so both routes mean the same thing. A
   * draft-only result commits via skipEnrichment like the draft-write branch of
   * importRowWithDraft; removing an element needs the full enrich pass.
   */
  const unsetImportRow = useCallback(
    (row: CandidateRow) => {
      const version = getSelectedVersion();
      if (!version) return;
      const changes = buildUnmapImportRowsChanges(version, [row.id]);
      if (changes.length === 0) return;
      const draftOnly = changes.every(
        (change) => change.type === ProjectChangeType.VersionImports,
      );
      void applyChanges(changes, { skipEnrichment: draftOnly }).catch(() => {
        enqueueSnackbar('Import failed while applying changes', {
          variant: 'error',
        });
      });
    },
    [applyChanges, enqueueSnackbar],
  );

  const importRows = useCallback(
    (rows: readonly CandidateRow[], trigger: 'auto' | 'user') => {
      const { changes, failedRows, needsProposal } = buildImportChanges(
        rows,
        trigger,
      );

      const failedMessage = buildFailedImportMessage(failedRows);
      if (failedMessage) enqueueSnackbar(failedMessage, { variant: 'error' });
      if (needsProposal) {
        enqueueSnackbar(IMPORT_REPLACE_NEEDS_PROPOSAL_MESSAGE, {
          variant: 'warning',
        });
        return;
      }
      if (changes.length === 0) return;
      void applyChanges(changes).catch(() => {
        enqueueSnackbar('Auto-import failed while applying changes', {
          variant: 'error',
        });
      });
    },
    [buildImportChanges, applyChanges, enqueueSnackbar],
  );

  return {
    importRows,
    buildImportChanges,
    importRowWithDraft,
    importRowsWithDrafts,
    unsetImportRow,
  };
};
